import { useTranslation } from "react-i18next";
import type { Integrator } from "../../../../../../store/reseller-admin/integratorsStore";
import type { ThemeClasses } from "../types";
import { useIsUserDev } from "../../../../../../hooks/useIsUserDev";

interface IntegratorListRowProps {
  integrator: Integrator;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onDetails: (integrator: Integrator) => void;
  onEdit: (integrator: Integrator) => void;
  onDelete: (integrator: Integrator) => void;
  editLoading?: boolean;
}

export default function IntegratorListRow({
  integrator,
  themeClasses,
  isDarkMode,
  onDetails,
  onEdit,
  onDelete,
  editLoading = false,
}: IntegratorListRowProps) {
  const { t } = useTranslation("resellerAdmin");
  const { isUserDev } = useIsUserDev();

  const protocolLabel = integrator.protocol
    ? t(`integrators.options.protocol.${integrator.protocol}`, {
        defaultValue: integrator.protocol,
      })
    : t("integrators.noData");

  return (
    <div
      className={`flex items-center gap-4 p-4 rounded-xl border ${themeClasses.cardBorder} ${themeClasses.cardBg} transition-all hover:shadow-md`}
    >
      <div
        className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
          isDarkMode ? "bg-primary-500/15" : "bg-primary-50"
        }`}
      >
        <i className="ri-plug-line text-lg text-primary-500"></i>
      </div>

      <div className="min-w-0 flex-1">
        <h3 className={`font-semibold truncate ${themeClasses.text}`}>
          {integrator.name}
        </h3>
        <p className={`text-xs truncate ${themeClasses.textSecondary}`}>
          {integrator.default_endpoint || t("integrators.noData")}
        </p>
      </div>

      <span className={`hidden sm:inline text-sm uppercase ${themeClasses.textSecondary}`}>
        {protocolLabel}
      </span>

      <span
        className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
          integrator.active
            ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
            : "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400"
        }`}
      >
        {integrator.active
          ? t("integrators.active")
          : t("integrators.inactive")}
      </span>

      <div className="flex items-center gap-1">
        <button
          onClick={() => onDetails(integrator)}
          className={`p-2 rounded-lg ${themeClasses.buttonHover} ${themeClasses.buttonText} transition-all`}
          title={t("integrators.actions.details")}
        >
          <i className="ri-eye-line"></i>
        </button>
        {isUserDev && (
          <>
            <button
              onClick={() => onEdit(integrator)}
              disabled={editLoading}
              className="p-2 rounded-lg text-primary-600 dark:text-primary-400 hover:bg-primary-500/20 transition-all disabled:opacity-60 disabled:cursor-wait"
              title={t("integrators.actions.edit")}
            >
              <i className={editLoading ? "ri-loader-4-line animate-spin" : "ri-edit-line"}></i>
            </button>
            <button
              onClick={() => onDelete(integrator)}
              className="p-2 rounded-lg text-red-500 hover:bg-red-100 dark:hover:bg-red-900/20 transition-all"
              title={t("integrators.actions.delete")}
            >
              <i className="ri-delete-bin-line"></i>
            </button>
          </>
        )}
      </div>
    </div>
  );
}
